import { useCallback, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { useLocale } from '../i18n/LocaleProvider';
import { auditEntryPath, operatorReason } from '../lib/audit-reason';
import { ConfirmDialog } from './ConfirmDialog';

/** One audited action waiting for its confirm step. */
export interface AuditReasonRequest {
  readonly title: string;
  readonly confirmLabel: string;
  /** What the action does, in the operator's words — the dialog's description. */
  readonly body: ReactNode;
  /** Runs the action with the trimmed reason. */
  readonly onConfirm: (reason: string) => void;
}

/**
 * The confirm-with-reason step every formerly unaudited queue and failed-job action now has
 * (work order AC-G4c).
 *
 * One dialog per page rather than one per button: `ask()` opens it for a request, the page renders
 * `dialog` once. The reason starts EMPTY for every request on purpose — carrying the last one over
 * would put the pause's "why" on the discard that followed it, and the audit trail is only as
 * good as the sentence on each row.
 */
export function useAuditReasonConfirm(): {
  readonly ask: (request: AuditReasonRequest) => void;
  readonly dialog: ReactNode;
} {
  const [request, setRequest] = useState<AuditReasonRequest | null>(null);
  const [reason, setReason] = useState('');

  const ask = useCallback((next: AuditReasonRequest) => {
    setReason('');
    setRequest(next);
  }, []);
  // Stable, because ConfirmDialog re-binds its Esc listener whenever `onCancel` changes.
  const cancel = useCallback(() => setRequest(null), []);

  const dialog = (
    <ConfirmDialog
      open={request !== null}
      title={request?.title ?? ''}
      confirmLabel={request?.confirmLabel ?? ''}
      onConfirm={() => {
        if (!request) return;
        setRequest(null);
        request.onConfirm(operatorReason(reason).reason);
      }}
      onCancel={cancel}
      reason={{ value: reason, onChange: setReason }}
    >
      {request?.body}
    </ConfirmDialog>
  );

  return { ask, dialog };
}

/**
 * The line after a successful audited action: the audit row it wrote, linked, or the plain fact
 * that none was written (a best-effort row that failed — the action still ran).
 */
export function AuditRecordedNote({
  auditLogId,
}: {
  readonly auditLogId: string | null | undefined;
}) {
  const { t, tNode } = useLocale();
  const path = auditEntryPath(auditLogId);
  if (!path || !auditLogId) {
    return <span className="block mt-1">{t('reasonPrompt.notRecorded')}</span>;
  }
  return (
    <span className="block mt-1">
      {tNode('reasonPrompt.recorded', {
        entry: (
          <Link to={path} className="font-mono underline">
            {auditLogId}
          </Link>
        ),
      })}
    </span>
  );
}
